import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * The Website Assistant: the team's screens (signed in) and the public chat a
 * visitor on the agency's website talks to (no sign-in: found by its widget
 * key, each conversation by its own token). Times are UTC ISO strings.
 */

// --- The team's screens ------------------------------------------------------------

/** Where a chat is. `left`: nobody has typed in it for half an hour. */
export type WaState = 'chatting' | 'left' | 'lead_sent' | 'lead_failed' | 'answered' | 'handed';

export interface WaCard {
  id: string;
  source: 'live' | 'seed';
  intent: 'quote' | 'question' | null;
  state: WaState;
  visitor: string | null;
  email: string | null;
  phone: string | null;
  zip: string | null;
  cars: string | null;
  started_at: string;
  last_at: string;
  completed_at: string | null;
  /** What the chat last asked, while it is still open: "Their email". */
  waiting_on: string | null;
  lead_id: number | null;
  task_id: number | null;
  handed_reason: string | null;
  error: string | null;
  /** AgencyZoom has not taken all of it yet: the lead, its documents or a call-back. */
  needs_sending_again: boolean;
  files: number;
  policy_num: string | null;
  question: string | null;
  /** Who the lead or call-back went to in AgencyZoom. */
  assigned_to: string | null;
}

export interface WaDetail extends WaCard {
  /** What will go, or went, on the lead: each with where it came from ("typed", or a document). */
  details: { label: string; value: string; from: string }[];
  answer: string | null;
  answer_source: string | null;
  client: { policy_num?: string; name?: string; line?: string } | null;
  documents: { id: string; name: string; kind: string | null; error: string | null; size: number }[];
  messages: { id: string; role: 'visitor' | 'assistant'; text: string; at: string; file: string | null }[];
}

export interface WaDay { day: string; value: number }

export interface WaSpotlight {
  tiles: {
    chats: WaDay[];
    leads: WaDay[];
    totals: {
      chats: number; leads: number; leads_from_documents: number; answered: number; handed: number;
      failed: number; chatting: number; median_minutes_to_lead: number | null;
    };
  };
  by_intent: { label: string; value: number }[];
  widget_path: string;
}

export interface WaSettings {
  agency_name: string;
  greeting: string;
  notice: string;
  brand_color: string;
  accent_color: string;
  font: string;
  locations: string;
  phone: string;
  email: string;
  pipeline_id: number | null;
  stage_id: number | null;
  lead_source_id: number | null;
  assign_to: number | null;
  lead_tags: string;
  policy_connector_id: string | null;
}

export interface WaConfiguration {
  settings: WaSettings;
  widget_path: string;
  agencyzoom: {
    pipelines: { id: number; name: string; stages: { id: number; name: string }[] }[];
    sources: { id: number; name: string }[];
    team: { id: number; name: string; email: string | null }[];
  };
  agencyzoom_problem: string | null;
  /** Where the next lead will go, by name, with the settings' blanks filled in. */
  leads_go_to: { pipeline: string | null; stage: string | null; lead_source: string | null; assign_to: string | null } | null;
  policy_system: string | null;
}

export interface WaIntegration {
  id: 'website' | 'agencyzoom' | 'policy_system';
  name: string;
  role: string;
  connected: boolean;
  detail: string;
  path?: string;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) };
}

function prefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return `${tenantApiPrefix(tid)}/website-assistant`;
}

async function call<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const response = await fetch(buildApiUrl(`${prefix()}${path}`), { ...init, headers: getAuthHeaders() });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallback);
  }
  return response.json();
}

export const fetchWaSpotlight = () => call<WaSpotlight>('/spotlight', { method: 'GET' }, 'Could not load the spotlight');
export const fetchWaConversations = (scope: 'live' | 'completed') =>
  call<{ conversations: WaCard[] }>(`/conversations?scope=${scope}`, { method: 'GET' }, 'Could not load the chats');
export const fetchWaConversation = (id: string) =>
  call<WaDetail>(`/conversations/${id}`, { method: 'GET' }, 'Could not load that chat');
/** Whatever AgencyZoom refused for this chat (the lead, its documents, or a call-back), sent again. */
export const sendWaAgain = (id: string) =>
  call<WaDetail>(`/conversations/${id}/send-again`, { method: 'POST' }, 'Could not send it to AgencyZoom');
export const fetchWaConfiguration = () =>
  call<WaConfiguration>('/configuration', { method: 'GET' }, 'Could not load the settings');
export const saveWaSettings = (change: Partial<WaSettings>) =>
  call<WaConfiguration>('/settings', { method: 'PATCH', body: JSON.stringify(change) }, 'Could not save the settings');
export const fetchWaIntegrations = () =>
  call<{ rows: WaIntegration[] }>('/integrations', { method: 'GET' }, 'Could not load the integrations');

/** A document a visitor sent, opened in a new tab. */
export async function openWaFile(fileId: string): Promise<void> {
  const response = await fetch(buildApiUrl(`${prefix()}/files/${fileId}`), { headers: getAuthHeaders() });
  if (!response.ok) throw new Error('Could not open that document');
  const url = URL.createObjectURL(await response.blob());
  window.open(url, '_blank', 'noopener');
}

// --- The public chat ---------------------------------------------------------------

export interface ChatChoice { value: string; label: string }

export interface ChatLine {
  id: string;
  role: 'visitor' | 'assistant';
  text: string;
  choices: ChatChoice[];
  at: string;
  file: string | null;
}

export interface ChatTurn { messages: ChatLine[]; step: string | null; status: string }

export interface ChatLook {
  agency_name: string;
  /** The website's own colours: headings and the chat's header; buttons and the visitor's messages. */
  brand_color: string;
  accent_color: string;
  /** A Google Fonts family, or empty for ours. */
  font: string;
  notice: string;
  /** What the stand-in website shows across its top. */
  locations: string[];
  phone: string;
  email: string;
  accepts: string[];
  max_bytes: number;
}

async function publicCall<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const response = await fetch(buildApiUrl(`/api/v1/assistant/${path}`), init);
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallback);
  }
  return response.json();
}

const chatHeaders = (token: string, json = true): HeadersInit =>
  ({ 'X-Chat-Token': token, ...(json && { 'Content-Type': 'application/json' }) });

export const fetchChatLook = (key: string) =>
  publicCall<ChatLook>(encodeURIComponent(key), { method: 'GET' }, 'This chat is not available');
export const startChat = (key: string) =>
  publicCall<ChatTurn & { conversation_id: string; token: string }>(
    `${encodeURIComponent(key)}/conversations`, { method: 'POST' }, 'The chat could not start. Please try again.');
export const readChat = (key: string, id: string, token: string) =>
  publicCall<ChatTurn>(`${encodeURIComponent(key)}/conversations/${id}`, { headers: chatHeaders(token, false) },
    'The chat could not be loaded');
export const sendChat = (key: string, id: string, token: string, said: { text?: string; choice?: string }) =>
  publicCall<ChatTurn>(`${encodeURIComponent(key)}/conversations/${id}/messages`,
    { method: 'POST', headers: chatHeaders(token), body: JSON.stringify(said) },
    'That message did not go through. Please try again.');
/** One or several documents, as one turn: the chat reads them together and answers once. */
export function sendChatFiles(key: string, id: string, token: string, files: File[]): Promise<ChatTurn> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  return publicCall<ChatTurn>(`${encodeURIComponent(key)}/conversations/${id}/files`,
    { method: 'POST', headers: chatHeaders(token, false), body: form },
    'That file did not go through. Please try again.');
}
